import type { UseFetchOptions } from 'nuxt/app'

function getHeaders(headers: any) {
  const additional: any = {}
  if (useRequestHeader('x-forwarded-for')) additional['x-forwarded-for'] = useRequestHeader('x-forwarded-for')
  return {
    ...(headers || {}),
    ...additional,
    'accept-language': useNuxtApp().$i18n.locale.value
  }
}

export const useServerFetch = (endpoint: string, options: UseFetchOptions<any> = {}, returnRef = false) => {
  return new Promise(async (resolve, reject) => {
    const config = useRuntimeConfig()
    const baseURL = process.server ? config.apiURL : config.public.apiURL
    options.headers = getHeaders(options.headers)
    const response = await useFetch(endpoint, {
      baseURL,
      watch: false,
      ...options,
      // getCachedData(key, nuxtApp) {
      //   return nuxtApp.payload.data[key]
      // }
    })

    if (response.error.value) reject(response.error.value)
    else resolve(!returnRef ? response.data.value : response.data)
  })
}

export const useClientFetch = (endpoint: string, options: UseFetchOptions<any> = {}) => {
  return new Promise(async (resolve, reject) => {
    options.headers = getHeaders(options.headers)
    try {
      const response = await $fetch(endpoint, {
        /* @ts-ignore */
        baseURL: useRuntimeConfig().public.apiURL,
        ...options
      })

      resolve(response)
    } catch (e) {
      reject(e)
    }
  })
}

type PaginatedDataOptions = {
  template: string
  templates: Array<string>
  endpoint: string
  howMany: number
  fields: Array<string>
  filters: any,
  auth: string
}

interface IResponseData {
  results: Array<any> | null
  count: number
  next: string | null
  previous: string | null
}

type PaginatedDataResponse = {
  data: IResponseData
  filter: (filters: any) => {}
  loadMore: (howMany: number) => {}
  loadNextPage: (howMany: number) => {}
  loadPreviousPage: (howMany: number) => {}
}

export const usePaginatedData = async (context: PaginatedDataOptions): Promise<PaginatedDataResponse> => {
  let params: any = {}
  let template = context.templates || (context.template ? [context.template] : undefined)
  let endpoint = undefined
  if (typeof context === 'string') {
    endpoint = context
  } else if (typeof context === 'object') {
    endpoint = context.endpoint
    params = {
      offset: 0,
      limit: context.howMany
    }

    if ('offset' in context) params.offset = context.offset
    if (context.fields && template) params.fields = context.fields.join(',')

    for (const key in context.filters) {
      if (context.filters[key] !== undefined) {
        params[key] = context.filters[key]
      }
    }
  }

  if (template) params.type = template.map(i => `common.${i}Page`).join(',')

  const options: [string, any] = [
    endpoint || 'v2/pages/',
    {
      params,
      transform
    }
  ]

  let data: any
  if(context.auth) {
    options[1].strategy = context.auth
    data = ref(await useAuthFetch(...options))
  } else data = await useServerFetch(...options, true)

  function transform(_data: any) {
    if (template) _data.results = _data.items

    if (_data?.meta) {
      _data.count = _data.meta.total_count
      _data.next = _data.count > _data.results.length
      delete _data.items
      delete _data.meta
    }

    return _data
  }

  function increaseOffset(howMany: number, dir = 1) {
    options[1].params.offset += options[1].params.limit * dir
    if (Number.isInteger(howMany)) options[1].params.limit = howMany
  }

  async function setHasNextPrevious() {
    try {
      const { offset, limit } = options[1].params
      data.value.next = offset + limit < data.value.count
      data.value.previous = options[1].params.offset > 0
    } catch (e) {}
  }

  async function loadMore(howMany: number) {
    increaseOffset(howMany)
    const { results } = transform(await _getData())
    data.value.results = [...data.value.results, ...results]
    setHasNextPrevious()
  }

  async function loadPage(howMany: number, dir = 1) {
    increaseOffset(howMany, dir)
    const { results } = transform(await _getData())
    data.value.results = results
    setHasNextPrevious()
  }

  async function loadNextPage(howMany: number) {
    await loadPage(howMany)
  }

  async function loadPreviousPage(howMany: number) {
    await loadPage(howMany, -1)
  }

  async function filter(filters: any) {
    options[1].params = {
      offset: 0,
      limit: context.howMany,
      ...filters
    }

    data.value = transform(await _getData())

    setHasNextPrevious()
  }

  async function _getData() {
    if(context.auth) {
      options[1].strategy = context.auth
      return await useAuthFetch(...options)
    }
    return await useClientFetch(...options)
  }

  return {
    data,
    filter,
    loadMore,
    loadNextPage,
    loadPreviousPage
  }
}
